import React, { useRef, useState } from 'react';
import { noop } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import ViewNotificationCard from '~/components/Notifications/ViewNotificationCard';
import { isoDateToUs } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import IncomeProtectionCreateNotificationDialog from './IncomeProtectionCreateNotificationDialog';
import TEXTS from './texts';
import type { MinimalNotificationType } from './types';

interface IncomeProtectionNotificationProps {
  existingNotification?: MinimalNotificationType | null;
  isNotificationTypeEnabled: boolean;
  createNotification: (due_date: string) => Promise<{ id: number } | null>;
  onNotificationUpdate: () => Promise<unknown> | unknown;
  notificationCreateDialogTitle: string;
  notificationCreateDialogSubtitle: string;
  defaultDueDateForNewNotification?: string | null;
}

const NotificationCtaWrapper = ({ isEnabled, children }: { isEnabled: boolean; children: React.ReactNode }) => {
  return isEnabled ? (
    <>{children}</>
  ) : (
    <Tooltip title={TEXTS.notificationDisabledWarning}>
      <span>{children}</span>
    </Tooltip>
  );
};

const IncomeProtectionNotification = ({
  existingNotification,
  isNotificationTypeEnabled: initialIsNotificationTypeEnabled,
  createNotification,
  onNotificationUpdate,
  notificationCreateDialogTitle,
  notificationCreateDialogSubtitle,
  defaultDueDateForNewNotification,
}: IncomeProtectionNotificationProps) => {
  const [isAddReminderDialogOpen, setIsAddReminderDialogOpen] = useState(false);
  const [isViewNotificationDialogOpen, setIsViewNotificationDialogOpen] = useState(false);
  const [isNotificationTypeEnabled, setIsNotificationTypeEnabled] = useState(initialIsNotificationTypeEnabled);
  const shouldUpdateOnClose = useRef(false);

  const handleCreateNotification = async (values: { due_date: string }) => {
    try {
      const result = await createNotification(values.due_date);
      if (result === null) {
        setIsNotificationTypeEnabled(false);
        return;
      }
      await onNotificationUpdate();
      setIsAddReminderDialogOpen(false);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const onUpdateNotification = () => {
    shouldUpdateOnClose.current = true;
  };

  const onCloseNotification = async () => {
    if (shouldUpdateOnClose.current) {
      shouldUpdateOnClose.current = false;
      await onNotificationUpdate();
    }
    setIsViewNotificationDialogOpen(false);
  };

  return (
    <>
      {existingNotification?.due_date ? (
        <div className="flex items-center gap-8 whitespace-nowrap">
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.notificationSetFor(isoDateToUs(existingNotification.due_date))}
          </Text>
          <NotificationCtaWrapper isEnabled={isNotificationTypeEnabled}>
            <IconButton onClick={() => setIsViewNotificationDialogOpen(true)} disabled={!isNotificationTypeEnabled}>
              <PencilIcon size={16} />
            </IconButton>
          </NotificationCtaWrapper>
        </div>
      ) : (
        <NotificationCtaWrapper isEnabled={isNotificationTypeEnabled}>
          <Button
            variant="text"
            color="primary"
            disabled={!isNotificationTypeEnabled}
            onClick={() => setIsAddReminderDialogOpen(true)}
            className="whitespace-nowrap"
          >
            {TEXTS.setNotification}
          </Button>
        </NotificationCtaWrapper>
      )}
      <IncomeProtectionCreateNotificationDialog
        open={isAddReminderDialogOpen}
        onClose={() => setIsAddReminderDialogOpen(false)}
        onSubmit={handleCreateNotification}
        title={notificationCreateDialogTitle}
        subtitle={notificationCreateDialogSubtitle}
        notificationTypeDisabled={!isNotificationTypeEnabled}
        initialDueDate={defaultDueDateForNewNotification}
      />
      {isViewNotificationDialogOpen && existingNotification ? (
        <ViewNotificationCard
          notification={existingNotification}
          // onClose is internally run after inner form updates are made.
          onClose={onCloseNotification}
          onUpdate={onUpdateNotification}
          onUpdateNotification={onUpdateNotification}
          onMarkAsUnread={noop}
        />
      ) : null}
    </>
  );
};

export default IncomeProtectionNotification;
